// @ts-strict

import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { IPostbackConfigFieldData } from '@app/core/models/core-models';
import { validationExpressions } from '@app/core/helpers/constants';
import { takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-postback-url',
  templateUrl: './postback-url.component.html',
  styleUrls: ['./postback-url.component.scss']
})
export class PostbackUrlComponent implements OnInit, OnChanges {
  @Input() usedParameters: string[] = [];
  @Input() parameterAdded: boolean = false;
  @Input() removedParam?: string;
  @Input() idOfRemovedParam?: number;
  @Input() editData?: IPostbackConfigFieldData;
  @Input() submitted: boolean = false;

  @Output() isUrlValid = new EventEmitter();

  constructor(private fb: UntypedFormBuilder) {}

  public urlAddressForm = this.fb.group({
    urlAddress: [null, [Validators.required, this.validateURL]]
  });
  public focused = false;

  public get form(): Record<string, AbstractControl> {
    return this.urlAddressForm.controls;
  }

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.urlAddressForm.statusChanges
      .pipe(
        tap(status => {
          this.isUrlValid.emit(status === 'VALID');
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    if (this.editData && Object.keys(this.editData).length) {
      this.form.urlAddress.setValue(this.editData.value);
    }
  }

  ngOnChanges(): void {
    if (!this.submitted) {
      let currentValue = this.form?.urlAddress.value;
      if (this.parameterAdded) {
        const lastValue = this.usedParameters[this.usedParameters.length - 1];
        // Ensure currentValue is not undefined or null
        currentValue = currentValue || '';
        // Check if the URL already contains a query string
        const separator = currentValue.includes('?') ? '&' : '?';
        const generatedURL = `${currentValue}${separator}${lastValue}=[${lastValue}]`;
        this.form.urlAddress.patchValue(generatedURL);
      } else if (this.removedParam) {
        if (currentValue?.includes(`[${this.removedParam}]`)) {
          const regex = new RegExp(`[?&]${this.removedParam}=[^&]*`);
          let updatedValue = currentValue.replace(regex, '');
          // removing excess '&' and '?' characters
          updatedValue = updatedValue.replace(/&+/g, '&').replace(/\?+/g, '?');
          // checking if '?' needs to be added back at the beginning
          if (!updatedValue.includes('?') && updatedValue.includes('&')) {
            updatedValue = updatedValue.replace('&', '?');
          }
          // removing a lone '?' or '&' character at the end of the URL
          updatedValue = updatedValue.replace(/[\?&]$/, '');
          this.form.urlAddress.patchValue(updatedValue);
        }
      }
    }
  }

  private validateURL(control: AbstractControl) {
    const isValid = control.value && control.value.match(validationExpressions.url);

    return isValid ? null : { urlInvalid: true };
  }
}
